import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalOverlay,
    ModalCloseButton,
    useBoolean
} from '@chakra-ui/react';
import ModalDeploy from './ModalDeploy';
import {
    DatabaseConfigEnv,
    DatabaseDeployTarget,
    RecordDatabaseConnection,
    SqlContentEntity,
    TransportRequestScriptDB,
    TransportRequestTag,
    UpdateTransportRequestResult,
    WebSocketDeploymentRequest,
    WebSocketDeploymentResponse
} from '../../../../data/schemas/TransportRequest';
import { FappConfig } from '../../../../models/FappMonitor';
import RunScriptDB from './RunScriptDB';
import { useStore } from 'commons/hooks';

interface NavigatorProps {
    isOpenModal: boolean;
    toggleModal: () => void;
    selectedDeploy: TransportRequestTag;
    handleConfirmDeploy: (portList: string[], uatTag: string) => void;
    deployTransportRequestResult: UpdateTransportRequestResult;
    fappPorts: FappConfig[];
    fetchScriptDBContents: () => Promise<Array<TransportRequestScriptDB & { error?: Error }>>;
    fetchDatabaseConfigEnvs: (isProd: boolean) => Promise<DatabaseConfigEnv[] | undefined>;
    onConnectDatabase: (payload: WebSocketDeploymentRequest) => void;
    handleDeployCompany: (uatTag: TransportRequestTag, company: string[]) => void;
    getConnectedDatabases: () => RecordDatabaseConnection[];
    onClickRunQuery: (payload: WebSocketDeploymentRequest) => void;
    consoleData: WebSocketDeploymentResponse[];
}

function NavigatorTransportRequest(props: NavigatorProps) {
    const {
        isOpenModal,
        toggleModal,
        deployTransportRequestResult,
        fappPorts,
        handleConfirmDeploy,
        selectedDeploy,
        fetchScriptDBContents,
        fetchDatabaseConfigEnvs,
        onConnectDatabase,
        handleDeployCompany,
        getConnectedDatabases,
        onClickRunQuery,
        consoleData
    } = props;

    const listCompany: { company: string; id: string }[] = [
        {
            company: 'EPS',
            id: 'eps'
        },
        {
            company: 'QMIX',
            id: 'qmix'
        },
        {
            company: 'SCIECO',
            id: 'scieco'
        },
        {
            company: 'SCG CM',
            id: 'scgcm'
        },
        {
            company: 'SLSN',
            id: 'slsn'
        },
        {
            company: 'SLTS',
            id: 'slts'
        },
        {
            company: 'SRIC',
            id: 'sric'
        },
        {
            company: 'TEX PLORE',
            id: 'tex'
        },
        {
            company: 'F1STD5_9001',
            id: '9001'
        },
        {
            company: 'F1STD5_9008',
            id: '9008'
        },
        {
            company: 'F1STD5_9009',
            id: '9009'
        },
        {
            company: 'F1STD5_9900',
            id: '9900'
        },
        {
            company: 'F1STD5_9998',
            id: '9998'
        },
        {
            company: 'F1STD5_8200',
            id: '8200'
        },
        {
            company: 'F1STD5_8201',
            id: '8201'
        },
        {
            company: 'F1STD5_8202',
            id: '8202'
        },
        {
            company: 'F1STD5_8100',
            id: '8100'
        },
        {
            company: 'F1STD5_8101',
            id: '8101'
        },
        {
            company: 'F1STD5_8102',
            id: '8102'
        },
        {
            company: 'F1STD5_8400',
            id: '8400'
        }
    ];

    const [isComponentLoading, setComponentLoading] = useBoolean();
    const [scriptDBContents, setScriptDBContents] = useState<
        Array<TransportRequestScriptDB & { error?: Error }>
    >([]);
    const [databaseConfigEnvs, setDatabaseConfigEnvs] = useState<DatabaseConfigEnv[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const { transportRequest } = useStore();
    const queryLogResult = transportRequest.queryLogs;

    const stdCompanies = listCompany.filter(c => c.company.startsWith('F1STD5'));
    const scgCompanies = listCompany.filter(c => !c.company.startsWith('F1STD5'));

    const isAllSelected = selectedIds.length === listCompany.length;
    const isAllStdSelected = stdCompanies.every(c => selectedIds.includes(c.id));
    const isAllScgSelected = scgCompanies.every(c => selectedIds.includes(c.id));

    useEffect(() => {
        setComponentLoading.on();

        if (selectedDeploy?.transportRequestDetail) {
            const initialIds = selectedDeploy.transportRequestDetail.flatMap(detail => {
                if (!detail.deployCompany) return [];

                return detail.deployCompany.split(',').map(company => company.trim());
            });

            setSelectedIds(Array.from(new Set(initialIds)));
        }

        (async () => {
            try {
                const results = await Promise.allSettled([
                    fetchScriptDBContents(),
                    fetchDatabaseConfigEnvs(false)
                ]);

                // results Output:
                // [
                //   { status: 'fulfilled', value: 'Result of A' },
                //   { status: 'rejected', reason: 'Error of B' }
                // ]

                // fetchScriptDBContents Result:
                if (results[0].status === 'fulfilled') {
                    // implement successfully
                    setScriptDBContents(results[0].value);
                } else {
                    // handle error
                    console.error('Error fetching script DB contents:', results[0].reason);
                }

                // fetchDatabaseConfigEnvs Result:
                if (results[1].status === 'fulfilled') {
                    setDatabaseConfigEnvs(results[1].value ?? []);
                } else {
                    // handle error
                    console.error(
                        'Error fetching database config environments:',
                        results[1].reason
                    );
                }
            } finally {
                setComponentLoading.off();
            }
        })();
    }, []);

    const handleConnectDatabase = (dbTargets: DatabaseDeployTarget[]) => {
        // Make a request payload
        const payload: WebSocketDeploymentRequest = {
            module: 'SCRIPTDB',
            scriptdb_info: {
                tag: selectedDeploy.uatTag,
                developer: '',
                client_action: 'connect',
                db_targets: dbTargets,
                sql_content: { s3_url: '', mssql_version: 0 },
                is_test_query: true
            }
        };

        // connect to databases
        onConnectDatabase(payload);
    };

    const handleRunQuery = (
        isRunTest: boolean,
        selectedFileScriptDb: SqlContentEntity,
        selectedDatabases: DatabaseDeployTarget[]
    ) => {
        // Make a request payload
        const payload: WebSocketDeploymentRequest = {
            module: 'SCRIPTDB',
            scriptdb_info: {
                tag: selectedDeploy.uatTag,
                developer: '',
                client_action: 'exec',
                db_targets: selectedDatabases,
                sql_content: {
                    s3_url: selectedFileScriptDb.s3_url,
                    mssql_version: 0
                },
                is_test_query: isRunTest
            }
        };

        // call execute
        console.log('Executing query...', payload);
        onClickRunQuery(payload);
    };

    const handleFetchQueryLogs = async () => {
        try {
            if (selectedDeploy?.uatTag) {
                console.log('Fetching query logs...', selectedDeploy.uatTag);
                await transportRequest.fetchQueryLogs(selectedDeploy.uatTag);
            }
        } catch (e) {
            if (e instanceof Error) {
                console.log('Get Query Logs error: ', e.message);
            }
        }
    };

    const handleToggle = (id: string) => {
        setSelectedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
    };

    const toggleAll = () => {
        if (isAllSelected) {
            setSelectedIds([]);
        } else {
            setSelectedIds(listCompany.map(c => c.id));
        }
    };

    const toggleAllStd = () => {
        const stdIds = stdCompanies.map(c => c.id);
        if (isAllStdSelected) {
            setSelectedIds(prev => prev.filter(id => !stdIds.includes(id)));
        } else {
            setSelectedIds(prev => Array.from(new Set([...prev, ...stdIds])));
        }
    };

    const toggleAllScg = () => {
        const scgIds = scgCompanies.map(c => c.id);
        if (isAllScgSelected) {
            setSelectedIds(prev => prev.filter(id => !scgIds.includes(id)));
        } else {
            setSelectedIds(prev => Array.from(new Set([...prev, ...scgIds])));
        }
    };

    return (
        <React.Fragment>
            <Modal
                isOpen={isOpenModal}
                onClose={toggleModal}
                size="xl"
                closeOnOverlayClick={false}
                scrollBehavior="inside"
            >
                <ModalOverlay />
                <ModalContent className="min-h-[85vh] min-w-[90vw]">
                    <ModalHeader>Deploy TransportRequest - {selectedDeploy.uatTag}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody className="max-h flex flex-col">
                        <Tabs
                            isLazy
                            variant="unstyled"
                            className="min-w-full"
                        >
                            <TabList>
                                <Tab _selected={{ color: 'white', bg: 'gray.600' }}>Deploy Log</Tab>
                                <Tab _selected={{ color: 'white', bg: 'green.400' }}>
                                    Run ScriptDB
                                </Tab>
                                <Tab
                                    _selected={{ color: 'white', bg: 'blue.500' }}
                                    isDisabled
                                >
                                    Update FAPP
                                </Tab>
                            </TabList>
                            <TabPanels>
                                <TabPanel className="p-0">
                                    <div className="bg-gray-50">
                                        <div className="mx-auto rounded-xl bg-white p-6 shadow-md">
                                            <h2 className="mb-6 border-b pb-2 text-xl font-bold text-gray-800">
                                                Company Selection
                                            </h2>
                                            <div className="mb-6">
                                                <button
                                                    onClick={() =>
                                                        handleDeployCompany(
                                                            selectedDeploy,
                                                            selectedIds
                                                        )
                                                    }
                                                    className={`w-full transform rounded-xl py-3 font-bold text-white shadow-lg transition-all active:scale-95
                                                    ${
                                                        selectedIds.length === 0
                                                            ? 'cursor-not-allowed bg-gray-300'
                                                            : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                                                    }`}
                                                    disabled={selectedIds.length === 0}
                                                >
                                                    Deploy
                                                </button>
                                            </div>

                                            <div className="mb-6 space-y-3 rounded-lg bg-blue-50 p-4">
                                                <label className="flex cursor-pointer items-center space-x-3">
                                                    <input
                                                        type="checkbox"
                                                        className="h-5 w-5 accent-blue-600"
                                                        checked={isAllSelected}
                                                        onChange={toggleAll}
                                                    />
                                                    <span className="font-semibold text-blue-800">
                                                        Select All (ทั้งหมด)
                                                    </span>
                                                </label>
                                                <label className="flex cursor-pointer items-center space-x-3">
                                                    <input
                                                        type="checkbox"
                                                        className="h-5 w-5 accent-indigo-600"
                                                        checked={isAllStdSelected}
                                                        onChange={toggleAllStd}
                                                    />
                                                    <span className="font-medium text-indigo-700">
                                                        All STD
                                                    </span>
                                                </label>
                                                <label className="flex cursor-pointer items-center space-x-3">
                                                    <input
                                                        type="checkbox"
                                                        className="h-5 w-5 accent-green-600"
                                                        checked={isAllScgSelected}
                                                        onChange={toggleAllScg}
                                                    />
                                                    <span className="font-medium text-green-700">
                                                        All SCG
                                                    </span>
                                                </label>
                                            </div>

                                            <div className="space-y-2 border-t pt-4">
                                                <p className="mb-2 text-sm text-gray-500">
                                                    Individual Companies ({selectedIds.length}{' '}
                                                    selected)
                                                </p>
                                                <div className="flex max-h-[400px] flex-col flex-wrap">
                                                    {listCompany.map((company, index) => (
                                                        <div
                                                            key={index}
                                                            className="flex min-w-fit max-w-md items-center justify-between text-wrap rounded p-2 transition-colors hover:bg-gray-100"
                                                        >
                                                            <label className="m-0 flex w-full cursor-pointer items-center space-x-3">
                                                                <input
                                                                    type="checkbox"
                                                                    className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                                                    checked={selectedIds.includes(
                                                                        company.id
                                                                    )}
                                                                    onChange={() =>
                                                                        handleToggle(company.id)
                                                                    }
                                                                />
                                                                <span
                                                                    className={
                                                                        selectedIds.includes(
                                                                            company.id
                                                                        )
                                                                            ? 'font-medium text-blue-600'
                                                                            : 'text-gray-700'
                                                                    }
                                                                >
                                                                    {company.company}
                                                                </span>
                                                            </label>
                                                            {company.company.startsWith(
                                                                'F1STD5'
                                                            ) && (
                                                                <span className="rounded bg-indigo-100 px-2 py-1 text-[10px] text-indigo-600">
                                                                    STD
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPanel>
                                <TabPanel className="p-0">
                                    <RunScriptDB
                                        scriptDBContents={scriptDBContents}
                                        databaseConfigEnvs={databaseConfigEnvs}
                                        isLoading={isComponentLoading}
                                        onClickConnectDatabase={databases =>
                                            handleConnectDatabase(databases)
                                        }
                                        databasesConnected={getConnectedDatabases()}
                                        onClickRunQuery={(isRunTest, scriptDB, databases) =>
                                            handleRunQuery(isRunTest, scriptDB, databases)
                                        }
                                        consoleData={consoleData}
                                        handleFetchQueryLogs={handleFetchQueryLogs}
                                        queryLogResult={queryLogResult}
                                    />
                                </TabPanel>
                                <TabPanel className="p-0">
                                    <ModalDeploy
                                        selectedDeploy={selectedDeploy}
                                        handleConfirmDeploy={handleConfirmDeploy}
                                        deployTransportRequestResult={deployTransportRequestResult}
                                        fappPorts={fappPorts}
                                    />
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </ModalBody>
                    <ModalFooter></ModalFooter>
                </ModalContent>
            </Modal>
        </React.Fragment>
    );
}

export default observer(NavigatorTransportRequest);
